import { useQuery } from '@tanstack/react-query';

import { useHttpClient } from '~/hooks/useHttpClient';
import { SessionStatus, Student } from '~/services/types';
import { determineRetryAndMaybeLog } from '~/utils/errors';
import { createLogger } from '~/utils/logging';

import { useReviewParams } from './useReviewParams';

const log = createLogger('useInsightsQuery');

export const QUERY_KEY = 'insights';

export type InsightsRequest = {
  date: Date;
  periodId: string;
};

export type InsightsResponse = {
  session: {
    status: SessionStatus;
  };
  periodId: string;
  weekStart: Date;
  weekEnd: Date;
  visibleFrom: Date;
  visibleTo: Date;
  sessionCount: number;
  insights: { students: Student[] }[];
};

export function useInsights({ date, periodId }: InsightsRequest) {
  const httpClient = useHttpClient();
  const params = useReviewParams({ date, periodId });

  const paramsQueryStr = params.toQueryString();

  const { data, error, status, refetch } = useQuery<null | InsightsResponse>({
    queryKey: [QUERY_KEY, paramsQueryStr],
    queryFn: async () => {
      log.debug('Fetching insights');

      const response = await httpClient.get<InsightsResponse>(`/review/getInsights?${paramsQueryStr}`);
      log.info('Fetched insights successfully', {
        periodId: response.periodId,
        sessionCount: response.sessionCount,
        insightCount: response.insights.length,
        studentCount: response.insights.reduce((total, i) => total + i.students.length, 0),
      });

      return response;
    },
    retry: (failureCount, error) =>
      determineRetryAndMaybeLog({ operation: 'fetch insights', failureCount, error, log }),
    enabled: httpClient.isAuthenticated,
  });

  return { data, error, status, refetch };
}
